import { render } from '@/threejs/core';
import { edgesManager } from '@/threejs/managers/edgesManager';
import { getObjectByCode } from '@/threejs/objectManager';
import gsap from 'gsap';
import * as THREE from 'three';

// Base class for all objects
const textureLoader = new THREE.TextureLoader();
export class BaseObject extends THREE.Mesh {
  constructor(geometry, material) {
    super(geometry, material);
  }

  setColor(color) {
    if (this.material) {
      this.material.color.set(new THREE.Color(color));
    }
  }

  getColor() {
    return this.material ? this.material.color.getHex() : null;
  }

  setSelected(selected) {
    this.userData.selected = selected;
  }

  isSelected() {
    return this.userData.selected;
  }

  select() {
    this.setSelected(true);
  }

  clearSelection() {
    this.setSelected(false);
  }
  removeOutline() {
    this.userData.edgeOutline = false;
    const outlinesToRemove = this.children.filter((child) => child.constructor.name === 'FatLinesBatch');

    outlinesToRemove.forEach((outline) => {
      this.remove(outline);
      // Dispose of outline resources if needed
      if (outline.dispose) {
        outline.dispose();
      }
    });
  }
  getOutline() {
    this.removeOutline();

    this.userData.edgeOutline = true;
    this.traverse((child) => {
      child.userData.edgeOutline = true;
    });
    edgesManager.refreshFlb();
  }

  applyProperties(properties) {
    // To be overridden by subclasses
    applyProperties(this, properties, false);
  }
}

// Common default properties for all objects
export const DEFAULT_PROPS = {
  name: 'New Object',
  position: { x: 0, y: 0, z: 0 },
  rotation: { x: 0, y: 0, z: 0 },
  scale: { x: 1, y: 1, z: 1 },
};

// Function to apply properties to an object
export const applyProperties = (object, properties, animate = true) => {
  // Find the mesh inside the group if this is a group

  if (!object) return;
  if (!object.userData) object.userData = {};
  if (!object.userData.data) object.userData.data = {};

  const mesh = object.children?.find((child) => child instanceof THREE.Mesh) || object;

  if (
    'position.x' in properties ||
    'position.y' in properties ||
    'position.z' in properties ||
    'position[x]' in properties ||
    'position[y]' in properties ||
    'position[z]' in properties
  ) {
    // Initialize position if it doesn't exist
    if (!object.userData.data.position) {
      object.userData.data.position = { x: 0, y: 0, z: 0 };
    }

    const needsUpdate =
      properties['position.x'] !== object.userData.data.position.x ||
      properties['position.y'] !== object.userData.data.position.y ||
      properties['position.z'] !== object.userData.data.position.z ||
      properties['position[x]'] !== object.userData.data.position.x ||
      properties['position[y]'] !== object.userData.data.position.y ||
      properties['position[z]'] !== object.userData.data.position.z;

    if (needsUpdate) {
      object.userData.data.position.x =
        Number(properties['position.x'] ?? properties['position[x]']) ?? object.userData.data.position.x ?? 0;
      object.userData.data.position.y =
        Number(properties['position.y'] ?? properties['position[y]']) ?? object.userData.data.position.y ?? 0;
      object.userData.data.position.z =
        Number(properties['position.z'] ?? properties['position[z]']) ?? object.userData.data.position.z ?? 0;

      animateObject(object.userData.code, { position: object.userData.data.position }, animate ? 0.1 : 0);
    }
  }

  if ('position' in properties) {
    // Initialize position if it doesn't exist
    if (!object.userData.data.position) {
      object.userData.data.position = { x: 0, y: 0, z: 0 };
    }

    const needsUpdate =
      properties.position.x !== object.userData.data.position.x ||
      properties.position.y !== object.userData.data.position.y ||
      properties.position.z !== object.userData.data.position.z;

    if (needsUpdate) {
      object.userData.data.position.x = Number(properties.position?.x ?? object.userData.data.position.x ?? 0);
      object.userData.data.position.y = Number(properties.position?.y ?? object.userData.data.position.y ?? 0);
      object.userData.data.position.z = Number(properties.position?.z ?? object.userData.data.position.z ?? 0);

      animateObject(object.userData.code, { position: object.userData.data.position }, animate ? 0.1 : 0);
    }
  }

  if (
    'rotation.x' in properties ||
    'rotation.y' in properties ||
    'rotation.z' in properties ||
    'rotation[x]' in properties ||
    'rotation[y]' in properties ||
    'rotation[z]' in properties
  ) {
    // Initialize rotation if it doesn't exist
    if (!object.userData.data.rotation) {
      object.userData.data.rotation = { x: 0, y: 0, z: 0 };
    }

    const needsUpdate =
      properties['rotation.x'] !== object.userData.data.rotation.x ||
      properties['rotation.y'] !== object.userData.data.rotation.y ||
      properties['rotation.z'] !== object.userData.data.rotation.z ||
      properties['rotation[x]'] !== object.userData.data.rotation.x ||
      properties['rotation[y]'] !== object.userData.data.rotation.y ||
      properties['rotation[z]'] !== object.userData.data.rotation.z;
    if (needsUpdate) {
      object.userData.data.rotation.x =
        Number(properties['rotation.x'] ?? properties['rotation[x]']) ?? object.userData.data.rotation.x ?? 0;
      object.userData.data.rotation.y =
        Number(properties['rotation.y'] ?? properties['rotation[y]']) ?? object.userData.data.rotation.y ?? 0;
      object.userData.data.rotation.z =
        Number(properties['rotation.z'] ?? properties['rotation[z]']) ?? object.userData.data.rotation.z ?? 0;
      animateObject(object.userData.code, { rotation: object.userData.data.rotation }, animate ? 0.1 : 0);
    }
  }

  if ('rotation' in properties) {
    // Initialize rotation if it doesn't exist
    if (!object.userData.data.rotation) {
      object.userData.data.rotation = { x: 0, y: 0, z: 0 };
    }

    const needsUpdate =
      properties.rotation.x !== object.userData.data.rotation.x ||
      properties.rotation.y !== object.userData.data.rotation.y ||
      properties.rotation.z !== object.userData.data.rotation.z;
    if (needsUpdate) {
      object.userData.data.rotation.x = Number(properties.rotation?.x ?? object.userData.data.rotation.x ?? 0);
      object.userData.data.rotation.y = Number(properties.rotation?.y ?? object.userData.data.rotation.y ?? 0);
      object.userData.data.rotation.z = Number(properties.rotation?.z ?? object.userData.data.rotation.z ?? 0);
      animateObject(object.userData.code, { rotation: object.userData.data.rotation }, animate ? 0.1 : 0);
    }
  }

  if ('scale' in properties) {
    // Initialize scale if it doesn't exist
    if (!object.userData.data.scale) {
      object.userData.data.scale = { x: 1, y: 1, z: 1 };
    }

    // Handle both numeric and vector3 scale values
    const scaleValue =
      typeof properties.scale === 'number'
        ? { x: properties.scale, y: properties.scale, z: properties.scale }
        : properties.scale;

    const needsUpdate =
      (scaleValue?.x ?? object.userData.data.scale.x) !== object.userData.data.scale.x ||
      (scaleValue?.y ?? object.userData.data.scale.y) !== object.userData.data.scale.y ||
      (scaleValue?.z ?? object.userData.data.scale.z) !== object.userData.data.scale.z;

    if (needsUpdate) {
      object.userData.data.scale.x = Number(scaleValue?.x ?? object.userData.data.scale.x ?? 1);
      object.userData.data.scale.y = Number(scaleValue?.y ?? object.userData.data.scale.y ?? 1);
      object.userData.data.scale.z = Number(scaleValue?.z ?? object.userData.data.scale.z ?? 1);

      animateObject(object.userData.code, { scale: object.userData.data.scale }, animate ? 0.1 : 0);
    }
  }
  if ('name' in properties && object.name !== properties.name) {
    object.name = properties.name;
  }

  if ('color' in properties) {
    if (mesh.material && properties.color) {
      const color = properties.color.startsWith('#') ? properties.color : `#${properties.color}`;

      const newColor = new THREE.Color(color);
      if (!mesh.material.color.equals(newColor)) {
        mesh.material.color.set(newColor);

        // Update emissive color if luminance is active
        const luminance = properties.luminance || mesh.userData.luminance || 0;
        if (luminance > 0 && properties.material !== 'map') {
          mesh.material.emissive = mesh.material.color.clone();
          mesh.material.emissiveIntensity = luminance / 100;
          mesh.material.needsUpdate = true;
        }
      }
    }
  }

  if ('luminance' in properties) {
    if (mesh.material) {
      const luminance = properties.luminance || 0;

      // Only update if luminance has changed
      if (mesh.userData.luminance !== luminance) {
        // Store luminance in userData for persistence
        mesh.userData.luminance = luminance;

        // Set emissive color to match the material color (or white if using a map)
        if (luminance > 0) {
          if (properties.material === 'map' && mesh.material.map) {
            // For textured materials, use white emissive
            mesh.material.emissive = new THREE.Color(0xffffff);
          } else {
            // For colored materials, use the same color for emissive
            mesh.material.emissive = mesh.material.color.clone();
          }
          // Convert luminance (cd/m2) to emissive intensity
          // Scale factor to make the values work well visually
          mesh.material.emissiveIntensity = luminance / 100;
        } else {
          // Turn off emissive when luminance is 0
          mesh.material.emissive = new THREE.Color(0x000000);
          mesh.material.emissiveIntensity = 0;
        }
        mesh.material.needsUpdate = true;
      }
    }
  }

  if ('visible' in properties && object.visible !== properties.visible) {
    object.visible = properties.visible;
  }

  if ('lock' in properties && object.userData.lock !== properties.lock) {
    object.userData.lock = properties.lock;
  }

  if ('side' in properties) {
    if (mesh.material && mesh.material.side !== properties.side) {
      mesh.material.side = properties.side;
    }
  }

  if ('material' in properties) {
    if (mesh.material) {
      if (properties.material === 'map' && properties.url) {
        const texture = textureLoader.load(properties.url);

        mesh.material.map = texture;
        if (properties.resource_id) {
          mesh.material.needsUpdate = true;
        }
      } else if (properties.material === 'color') {
        mesh.material.map = null;
        mesh.material.needsUpdate = true;
      }

      // Update emissive based on current luminance when material changes
      if ('luminance' in properties || mesh.userData.luminance) {
        const luminance = properties.luminance || mesh.userData.luminance || 0;
        if (luminance > 0) {
          if (properties.material === 'map' && mesh.material.map) {
            mesh.material.emissive = new THREE.Color(0xffffff);
          } else {
            mesh.material.emissive = mesh.material.color.clone();
          }
          mesh.material.emissiveIntensity = luminance / 100;
        }
      }
    }
  }

  if ('wireframe' in properties) {
    if (mesh.material) {
      mesh.material.wireframe = properties.wireframe;
      mesh.material.needsUpdate = true;
    }
  }

  if ('repeatX' in properties || 'repeatY' in properties) {
    if (mesh.material && mesh.material.map) {
      const newRepeatX = properties.repeatX || 1;
      const newRepeatY = properties.repeatY || 1;

      if (mesh.material.map.repeat.x !== newRepeatX || mesh.material.map.repeat.y !== newRepeatY) {
        mesh.material.map.repeat.set(newRepeatX, newRepeatY);
        mesh.material.map.needsUpdate = true;
      }
    }
  }

  if ('wrappingX' in properties || 'wrappingY' in properties) {
    if (mesh.material && mesh.material.map) {
      const getWrappingMode = (value) => {
        switch (value) {
          case 'repeat':
            return THREE.RepeatWrapping;
          case 'mirror':
            return THREE.MirroredRepeatWrapping;
          case 'normal':
          default:
            return THREE.ClampToEdgeWrapping;
        }
      };

      const newWrapS = getWrappingMode(properties.wrappingX);
      const newWrapT = getWrappingMode(properties.wrappingY);

      if (mesh.material.map.wrapS !== newWrapS || mesh.material.map.wrapT !== newWrapT) {
        mesh.material.map.wrapS = newWrapS;
        mesh.material.map.wrapT = newWrapT;
        mesh.material.map.needsUpdate = true;
      }
    }
  }

  if ('offsetX' in properties || 'offsetY' in properties) {
    if (mesh.material && mesh.material.map) {
      const newOffsetX = properties.offsetX || 0;
      const newOffsetY = properties.offsetY || 0;

      if (mesh.material.map.offset.x !== newOffsetX || mesh.material.map.offset.y !== newOffsetY) {
        mesh.material.map.offset.set(newOffsetX, newOffsetY);
        mesh.material.map.needsUpdate = true;
      }
    }
  }

  if ('playSource' in properties) {
    // Handle video playback control
    if (properties.playSource === 'stop') {
      object.stop();
    } else {
      object.play();
    }
  }

  if ('applyProperties' in object) {
    //console.warn('Calling super applyProperties on object', object)
    //object.applyProperties(properties)
  }

  render();
};

// Function to animate object properties
export const animateObject = (id, properties, duration = 0.1, ease = 'power2.inOut') => {
  const object = getObjectByCode(id);
  if (!object) return;

  // Create a GSAP timeline for the animation
  const timeline = gsap.timeline();

  // Animate position if provided
  if (properties.position) {
    timeline.to(object.position, {
      x: properties.position.x ?? object.position.x ?? 0,
      y: properties.position.y ?? object.position.y ?? 0,
      z: properties.position.z ?? object.position.z ?? 0,
      duration,
      ease,
      onUpdate: () => {
        render();
        edgesManager.refreshFlb();
      },
    });
  }

  // Animate rotation if provided
  if (properties.rotation) {
    // Convert rotation values from degrees to radians
    const rotationInRadians = {
      x: THREE.MathUtils.degToRad(properties.rotation.x || object.rotation.x || 0),
      y: THREE.MathUtils.degToRad(properties.rotation.y || object.rotation.y || 0),
      z: THREE.MathUtils.degToRad(properties.rotation.z || object.rotation.z || 0),
    };

    timeline.to(
      object.rotation,
      {
        x: rotationInRadians.x,
        y: rotationInRadians.y,
        z: rotationInRadians.z,
        duration,
        ease,
        onUpdate: () => render(),
      },
      '<'
    );
  }

  // Animate scale if provided
  if (properties.scale) {
    timeline.to(
      object.scale,
      {
        x: properties.scale.x || object.scale.x || 1,
        y: properties.scale.y || object.scale.y || 1,
        z: properties.scale.z || object.scale.z || 1,
        duration,
        ease,
        onUpdate: () => render(),
      },
      '<'
    );
  }

  return timeline;
};

// Function to stop all animations on an object
export const stopObjectAnimations = (id) => {
  const object = getObjectByCode(id);
  if (!object) return;

  gsap.killTweensOf(object.position);
  gsap.killTweensOf(object.rotation);
  gsap.killTweensOf(object.scale);
};
